import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, Folder, FolderOpen, FileText, Flag, Pencil, Trash2, FolderInput, MoreVertical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import type { KnowledgeBaseItem } from '@/services/knowledgeBase.service';
import { formatShortDate } from '@/utils/formatDate';

export type TreeNode = KnowledgeBaseItem & { children: TreeNode[] };

export function buildTree(items: KnowledgeBaseItem[]): TreeNode[] {
  const itemMap = new Map<string, TreeNode>();
  for (const item of items) {
    itemMap.set(item.id, { ...item, children: [] });
  }

  const roots: TreeNode[] = [];
  for (const node of itemMap.values()) {
    if (node.parent_id && itemMap.has(node.parent_id)) {
      itemMap.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.item_type !== b.item_type) return a.item_type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    nodes.forEach(n => sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

interface FolderTreeProps {
  items: KnowledgeBaseItem[];
  allItems?: KnowledgeBaseItem[];
  selectedId: string | null;
  onSelect: (item: KnowledgeBaseItem) => void;
  onCreateFolder: (parentId: string | null) => void;
  onCreateArticle: (parentId: string | null) => void;
  onRename: (item: KnowledgeBaseItem) => void;
  onMove: (item: KnowledgeBaseItem) => void;
  onDelete: (item: KnowledgeBaseItem) => void;
}

function countPendingInSubtree(node: TreeNode, pendingNewIds: Set<string>, pendingUpdateIds: Set<string>): number {
  let count = 0;
  if (node.item_type === 'article' && (pendingNewIds.has(node.id) || pendingUpdateIds.has(node.id))) count++;
  for (const child of node.children) {
    count += countPendingInSubtree(child, pendingNewIds, pendingUpdateIds);
  }
  return count;
}

function TreeNodeItem({
  node,
  depth,
  selectedId,
  expandedIds,
  pendingNewIds,
  pendingUpdateIds,
  toggleExpanded,
  onSelect,
  onCreateFolder,
  onCreateArticle,
  onRename,
  onMove,
  onDelete,
}: {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  expandedIds: Set<string>;
  pendingNewIds: Set<string>;
  pendingUpdateIds: Set<string>;
  toggleExpanded: (id: string) => void;
  onSelect: (item: KnowledgeBaseItem) => void;
  onCreateFolder: (parentId: string | null) => void;
  onCreateArticle: (parentId: string | null) => void;
  onRename: (item: KnowledgeBaseItem) => void;
  onMove: (item: KnowledgeBaseItem) => void;
  onDelete: (item: KnowledgeBaseItem) => void;
}) {
  const isFolder = node.item_type === 'folder';
  const isPending = node.status === 'pending';
  const hasPendingUpdate = !isFolder && pendingUpdateIds.has(node.id);
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const folderPendingCount = isFolder ? countPendingInSubtree(node, pendingNewIds, pendingUpdateIds) : 0;
  const dateTooltip = isFolder
    ? undefined
    : [
        formatShortDate(node.created_at) && `Created ${formatShortDate(node.created_at)}`,
        formatShortDate(node.last_updated || node.created_at) &&
          `Updated ${formatShortDate(node.last_updated || node.created_at)}`,
      ].filter(Boolean).join(' · ') || undefined;

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer text-xs hover:bg-muted',
          isSelected && 'bg-muted font-medium',
          isPending && 'opacity-70'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        title={dateTooltip}
        onClick={() => {
          if (isFolder) {
            toggleExpanded(node.id);
          }
          onSelect(node);
        }}
      >
        {isFolder ? (
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}

        {isFolder ? (
          isExpanded ? (
            <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
          )
        ) : (hasPendingUpdate || isPending) ? (
          <Flag className={cn(
            'h-4 w-4 shrink-0',
            hasPendingUpdate ? 'text-blue-600' : 'text-amber-600'
          )} />
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        <span className="truncate flex-1">{node.name || <span className="italic text-muted-foreground">&lt;Untitled&gt;</span>}</span>

        {isFolder && folderPendingCount > 0 && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0 text-amber-600 border-amber-400">
            {folderPendingCount}
          </Badge>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {isFolder && (
              <>
                <DropdownMenuItem onClick={() => onCreateArticle(node.id)}>
                  <FileText className="h-4 w-4 mr-2" />
                  New Article
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onCreateFolder(node.id)}>
                  <Folder className="h-4 w-4 mr-2" />
                  New Subfolder
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => onRename(node)}>
              <Pencil className="h-4 w-4 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMove(node)}>
              <FolderInput className="h-4 w-4 mr-2" />
              Move
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onDelete(node)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete{isFolder ? ' Folder' : ''}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isFolder && isExpanded && node.children.length > 0 && (
        <div>
          {node.children.map(child => (
            <TreeNodeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              pendingNewIds={pendingNewIds}
              pendingUpdateIds={pendingUpdateIds}
              toggleExpanded={toggleExpanded}
              onSelect={onSelect}
              onCreateFolder={onCreateFolder}
              onCreateArticle={onCreateArticle}
              onRename={onRename}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderTree({
  items,
  allItems,
  selectedId,
  onSelect,
  onCreateFolder,
  onCreateArticle,
  onRename,
  onMove,
  onDelete,
}: FolderTreeProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildTree(items), [items]);

  const { pendingNewIds, pendingUpdateIds } = useMemo(() => {
    const source = allItems ?? items;
    const newIds = new Set<string>();
    const updateIds = new Set<string>();
    for (const item of source) {
      if (item.item_type === 'article' && item.status === 'pending' && !item.original_item_id) {
        newIds.add(item.id);
      }
      if (item.original_item_id && item.status === 'pending') {
        updateIds.add(item.original_item_id);
      }
    }
    const itemIds = new Set(source.map(r => r.id));
    for (const id of updateIds) {
      if (!itemIds.has(id)) updateIds.delete(id);
    }
    return { pendingNewIds: newIds, pendingUpdateIds: updateIds };
  }, [allItems, items]);

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-0.5 px-2 py-1">
      {tree.length === 0 && (
        <p className="text-sm text-muted-foreground px-4 py-8 text-center">
          No items yet. Create a folder or article to get started.
        </p>
      )}
      {tree.map(node => (
        <TreeNodeItem
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          expandedIds={expandedIds}
          pendingNewIds={pendingNewIds}
          pendingUpdateIds={pendingUpdateIds}
          toggleExpanded={toggleExpanded}
          onSelect={onSelect}
          onCreateFolder={onCreateFolder}
          onCreateArticle={onCreateArticle}
          onRename={onRename}
          onMove={onMove}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
